import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

const GEARBOX_ISSUES = [
  "Svårt att växla",
  "Gnisslande",
  "Lös växelspak",
  "Annat",
] as const;

interface GearboxSectionProps {
  gearbox: { problem?: string; issue?: string; other?: string } | null;
  onGearboxChange: (gearbox: {
    problem?: string;
    issue?: string;
    other?: string;
  }) => void;
  onNext: () => void;
}

export const GearboxSection = ({
  gearbox,
  onGearboxChange,
  onNext,
}: GearboxSectionProps) => {
  const [otherText, setOtherText] = useState(gearbox?.other || "");
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const form = ct.form ?? {};
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};

  const handleResultChange = (problem: string) => {
    if (problem === "no") {
      onGearboxChange({ problem });
    } else {
      onGearboxChange({ problem, issue: undefined, other: undefined });
    }
  };

  const handleIssueChange = (issue: string) => {
    if (issue === "Annat") {
      onGearboxChange({ ...gearbox, issue, other: otherText });
    } else {
      onGearboxChange({ ...gearbox, issue, other: undefined });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (gearbox?.issue === "Annat") {
      onGearboxChange({ ...gearbox, other: text });
    }
  };

  const showNextButton =
    gearbox?.problem === "yes" &&
    gearbox?.issue === "Annat" &&
    otherText.trim();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-sm mb-3">{form.questionGearbox}</h4>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          <Button
            onClick={() => handleResultChange("yes")}
            variant="formOption"
            className={`${
              gearbox?.problem === "yes"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.yes}
          </Button>
          <Button
            onClick={() => handleResultChange("no")}
            variant="formOption"
            className={`${
              gearbox?.problem === "no"
                ? "border-accent border-2"
                : "border-gray-400"
            }`}
          >
            {v.no}
          </Button>
        </div>

        {gearbox?.problem === "yes" && (
          <div className="space-y-3">
            <h5 className="font-medium text-sm">{form.questionWhichProblem}</h5>
            <div className="grid grid-cols-2 gap-1.5">
              {GEARBOX_ISSUES.map((issue) => (
                <Button
                  key={issue}
                  onClick={() => handleIssueChange(issue)}
                  variant="formOption"
                  className={`${
                    gearbox?.issue === issue
                      ? "border-accent border-2"
                      : "border-gray-400"
                  }`}
                >
                  {opt[issue] ?? issue}
                </Button>
              ))}
            </div>

            {gearbox?.issue === "Annat" && (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={form.placeholderDescribeProblem}
                  value={otherText}
                  onChange={(e) => handleOtherTextChange(e.target.value)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        )}

        {showNextButton && (
          <Button className="w-full mt-4" onClick={onNext}>
            {form.next}
          </Button>
        )}
      </div>
    </div>
  );
};
